import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import api from "../lib/api";

const typeLabels = {
  diurna: "Diurna",
  nocturna: "Nocturna",
  mixta: "Mixta",
};

const typeColors = {
  diurna: "bg-amber-50 text-amber-700 border-amber-200",
  nocturna: "bg-indigo-50 text-indigo-700 border-indigo-200",
  mixta: "bg-violet-50 text-violet-700 border-violet-200",
};

const emptyForm = {
  name: "",
  schedule_type: "diurna",
};

export default function Schedules({ embedded = false }) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get("/schedules");
      setSchedules(data);
    } catch {
      toast.error("Error al cargar horarios");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (s) => {
    setEditId(s.id);
    setForm({
      name: s.name,
      schedule_type: s.schedule_type || "diurna",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name) {
      toast.error("Nombre es requerido");
      return;
    }
    try {
      const payload = {
        name: form.name,
        schedule_type: form.schedule_type,
      };
      if (editId) {
        await api.put(`/schedules/${editId}`, payload);
        toast.success("Horario actualizado");
      } else {
        await api.post("/schedules", payload);
        toast.success("Horario creado");
      }
      setDialogOpen(false);
      fetchData();
    } catch {
      toast.error("Error al guardar horario");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este horario?")) return;
    try {
      await api.delete(`/schedules/${id}`);
      toast.success("Horario eliminado");
      fetchData();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  return (
    <div data-testid="schedules-page">
      <div className="flex items-center justify-between mb-6">
        {!embedded && (
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950">
              Horarios y Jornadas
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              Catalogo de jornadas. El detalle de horas se define por trabajador.
            </p>
          </div>
        )}
        {embedded && <div />}
        <Button
          onClick={openCreate}
          className="bg-zinc-900 text-white hover:bg-zinc-800"
          data-testid="add-schedule-button"
        >
          <Plus className="w-4 h-4 mr-2" /> Nuevo horario
        </Button>
      </div>

      <Card className="bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-100 hover:bg-transparent">
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Nombre
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Tipo de jornada
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide text-right">
                  Acciones
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {schedules.map((s) => (
                <TableRow
                  key={s.id}
                  className="border-zinc-100"
                  data-testid={`schedule-row-${s.id}`}
                >
                  <TableCell className="text-sm text-zinc-900 font-medium">
                    {s.name}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={`font-medium ${typeColors[s.schedule_type] || ""}`}
                    >
                      {typeLabels[s.schedule_type] || s.schedule_type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(s)}
                      data-testid={`edit-schedule-${s.id}`}
                    >
                      <Pencil className="w-4 h-4 text-zinc-400" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(s.id)}
                      data-testid={`delete-schedule-${s.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {schedules.length === 0 && !loading && (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    className="text-center py-16 text-zinc-400"
                  >
                    <Clock className="w-10 h-10 mx-auto mb-3 opacity-20" />
                    <p className="text-sm">No hay horarios registrados</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-950">
              {editId ? "Editar horario" : "Nuevo horario"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-xs font-medium text-zinc-700">
                Nombre *
              </Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jornada Diurna Regular"
                data-testid="schedule-name-input"
              />
            </div>

            <div>
              <Label className="text-xs font-medium text-zinc-700">
                Tipo de jornada
              </Label>
              <Select
                value={form.schedule_type}
                onValueChange={(v) => setForm({ ...form, schedule_type: v })}
              >
                <SelectTrigger data-testid="schedule-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="diurna">Diurna</SelectItem>
                  <SelectItem value="nocturna">Nocturna</SelectItem>
                  <SelectItem value="mixta">Mixta</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-zinc-400 mt-1.5">
                Las horas ordinarias, descansos y dias laborales se definen por
                trabajador en el asistente de registro.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              data-testid="schedule-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="schedule-submit-button"
            >
              {editId ? "Actualizar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
